/**
 * MyPinnedQuotes Component
 *
 * Displays user's pinned quotes ("My Jobs") in a sticky header.
 * Shows quote info: quote #, project name, stage, ship date, PM, POA/Comps counts.
 * Data stored in backend database (cross-device).
 * Max 10 pinned quotes.
 */

import { useState, useEffect, useCallback, createContext, useContext } from "react";

// Context for sharing pinned quotes state across components
const PinnedQuotesContext = createContext(null);

export function PinnedQuotesProvider({ children }) {
  const [pinnedQuotes, setPinnedQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [enrichedQuotes, setEnrichedQuotes] = useState([]);

  // Fetch pinned quotes from backend
  const fetchPinnedQuotes = useCallback(async () => {
    try {
      const token = sessionStorage.getItem("msal.backendToken");
      if (!token) return;

      const res = await fetch("/api/pinned-quotes", {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (res.ok) {
        const data = await res.json();
        setPinnedQuotes(data.pinnedQuotes || []);
      }
    } catch (err) {
      console.error("Error fetching pinned quotes:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  // Pin a quote
  const pinQuote = useCallback(async (quoteNumber) => {
    try {
      const token = sessionStorage.getItem("msal.backendToken");
      if (!token) return { success: false, error: "Not authenticated" };

      const res = await fetch(`/api/pinned-quotes/${quoteNumber}`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });

      const data = await res.json();
      if (res.ok && data.pinnedQuotes) {
        setPinnedQuotes(data.pinnedQuotes);
      }
      return data;
    } catch (err) {
      console.error("Error pinning quote:", err);
      return { success: false, error: err.message };
    }
  }, []);

  // Unpin a quote
  const unpinQuote = useCallback(async (quoteNumber) => {
    try {
      const token = sessionStorage.getItem("msal.backendToken");
      if (!token) return { success: false, error: "Not authenticated" };

      const res = await fetch(`/api/pinned-quotes/${quoteNumber}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });

      const data = await res.json();
      if (res.ok && data.pinnedQuotes) {
        setPinnedQuotes(data.pinnedQuotes);
      }
      return data;
    } catch (err) {
      console.error("Error unpinning quote:", err);
      return { success: false, error: err.message };
    }
  }, []);

  // Check if a quote is pinned
  const isQuotePinned = useCallback((quoteNumber) => {
    return pinnedQuotes.some(p => p.quoteNumber === quoteNumber?.toString());
  }, [pinnedQuotes]);

  // Load on mount
  useEffect(() => {
    fetchPinnedQuotes();
  }, [fetchPinnedQuotes]);

  const value = {
    pinnedQuotes,
    enrichedQuotes,
    setEnrichedQuotes,
    loading,
    pinQuote,
    unpinQuote,
    isQuotePinned,
    refreshPinnedQuotes: fetchPinnedQuotes,
  };

  return (
    <PinnedQuotesContext.Provider value={value}>
      {children}
    </PinnedQuotesContext.Provider>
  );
}

export function usePinnedQuotes() {
  const context = useContext(PinnedQuotesContext);
  // Return no-op functions if not inside provider (graceful degradation)
  if (!context) {
    console.warn("usePinnedQuotes: Not inside PinnedQuotesProvider, pin functionality disabled");
    return {
      pinnedQuotes: [],
      enrichedQuotes: [],
      loading: false,
      pinQuote: async () => ({ success: false, error: "Provider not available" }),
      unpinQuote: async () => ({ success: false, error: "Provider not available" }),
      isQuotePinned: () => false,
      refreshPinnedQuotes: () => {},
    };
  }
  return context;
}

/**
 * MyPinnedQuotes display component
 */
export default function MyPinnedQuotes({ allQuotes = [], onSelectQuote }) {
  const { pinnedQuotes, enrichedQuotes, setEnrichedQuotes, loading, unpinQuote } = usePinnedQuotes();
  const [isCollapsed, setIsCollapsed] = useState(false);

  // Enrich pinned quotes with full quote data
  useEffect(() => {
    if (pinnedQuotes.length === 0 || allQuotes.length === 0) {
      setEnrichedQuotes([]);
      return;
    }

    const enriched = pinnedQuotes.map(pinned => {
      const fullQuote = allQuotes.find(q =>
        q.quoteNumber?.toString() === pinned.quoteNumber
      );
      return {
        ...pinned,
        quote: fullQuote || null,
      };
    }).filter(p => p.quote); // Only show quotes we have data for

    setEnrichedQuotes(enriched);
  }, [pinnedQuotes, allQuotes, setEnrichedQuotes]);

  // Don't render if no pinned quotes
  if (loading || enrichedQuotes.length === 0) {
    return null;
  }

  return (
    <div className="sticky top-0 z-40 bg-slate-200 backdrop-blur border-b border-gray-200 -mx-4 px-4 py-2">
      {/* Header */}
      <div
        className="flex items-center justify-between cursor-pointer hover:bg-slate-200 rounded px-2 py-1 -mx-2"
        onClick={() => setIsCollapsed(!isCollapsed)}
      >
        <div className="flex items-center gap-2">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-4 w-4 text-amber-500"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path d="M5 4a2 2 0 012-2h6a2 2 0 012 2v14l-5-2.5L5 18V4z" />
          </svg>
          <span className="text-sm font-medium text-gray-700">My Jobs</span>
          <span className="px-1.5 py-0.5 bg-amber-600/30 text-amber-600 rounded text-xs">
            {enrichedQuotes.length}
          </span>
        </div>
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className={`h-4 w-4 text-gray-500 transition-transform ${isCollapsed ? "" : "rotate-180"}`}
          viewBox="0 0 20 20"
          fill="currentColor"
        >
          <path
            fillRule="evenodd"
            d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
            clipRule="evenodd"
          />
        </svg>
      </div>

      {/* Pinned Quote Cards */}
      {!isCollapsed && (
        <div className="flex flex-wrap gap-2 mt-2">
          {enrichedQuotes.map(({ quoteNumber, quote }) => (
            <div
              key={quoteNumber}
              className="group flex items-center gap-3 px-3 py-2 rounded-lg border bg-slate-200 border-gray-300/40 hover:border-amber-500/50 hover:bg-slate-100 transition-all cursor-pointer"
              onClick={() => onSelectQuote?.(quote)}
            >
              {/* Quote # and Project Name */}
              <div className="flex flex-col min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-xs text-amber-400 font-bold">
                    #{quoteNumber}
                  </span>
                  {quote.user_Date1 && (
                    <span className={`text-[10px] ${getShipDateColor(quote.user_Date1)}`}>
                      {formatShipDate(quote.user_Date1)}
                    </span>
                  )}
                </div>
                <span className="text-xs text-gray-700 truncate max-w-[150px]" title={quote.user_Text4}>
                  {quote.user_Text4 || "No project name"}
                </span>
              </div>

              {/* Customer & Sales Rep */}
              <div className="flex flex-col text-[10px] border-l border-gray-300/50 pl-2">
                <span className="text-gray-500 truncate max-w-[80px]" title={quote.customerName}>
                  {quote.customerName || "—"}
                </span>
                <span className="text-gray-500 truncate max-w-[80px]" title={quote.salesID}>
                  {quote.salesID || "—"}
                </span>
              </div>

              {/* Unpin button */}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  unpinQuote(quoteNumber);
                }}
                className="opacity-0 group-hover:opacity-100 p-1 text-gray-500 hover:text-red-400 rounded hover:bg-slate-100 transition-all flex-shrink-0"
                title="Unpin"
              >
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// Helper: format ship date
function formatShipDate(dateStr) {
  if (!dateStr) return "";
  try {
    const date = new Date(dateStr);
    return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  } catch {
    return "";
  }
}

// Helper: get ship date color based on urgency
function getShipDateColor(dateStr) {
  if (!dateStr) return "text-gray-500";
  try {
    const date = new Date(dateStr);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const diffDays = Math.ceil((date - today) / (1000 * 60 * 60 * 24));

    if (diffDays < 0) return "text-red-400";
    if (diffDays === 0) return "text-green-400";
    if (diffDays <= 3) return "text-orange-400";
    if (diffDays <= 7) return "text-yellow-400";
    return "text-gray-500";
  } catch {
    return "text-gray-500";
  }
}
